'use client';

import Link from 'next/link';
import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import ScrollFadeIn from '@/components/ui/ScrollFadeIn';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.25, 0.4, 0.25, 1] as const },
  }),
};

function InViewSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const pillars = [
  { tag: 'EASM', name: 'External Attack Surface Management', desc: 'Continuously discover and test everything you expose to the internet: domains, cloud assets, APIs, and forgotten services.' },
  { tag: 'DAST', name: 'Dynamic Application Security Testing', desc: 'Test your running web apps and APIs the way an attacker would.' },
  { tag: 'SAST', name: 'Static Application Security Testing', desc: 'Scan source code for vulnerabilities before they ship.' },
  { tag: 'Pentest', name: 'On-demand penetration test', desc: 'A full pentest in minutes, not once a year. Evidence-backed, with zero false positives.' },
];

const howItWorks = [
  {
    num: '01',
    title: 'Enter a Domain and Go',
    body: 'Enter your domain and we handle the rest. Prove ownership with a quick DNS TXT record, the same way Google Search Console works. Optionally add IP ranges or specific subdomains to scope.',
  },
  {
    num: '02',
    title: 'AutoScan Attacks Your Surface',
    body: 'AI agents orchestrate hundreds of specialized tools across thousands of assets, actively attempting exploitation, not just scanning. Default credentials tried. SQL injection payloads sent. Real evidence collected.',
  },
  {
    num: '03',
    title: 'Auditor-Ready Report in 20 min',
    body: 'Executive summary plus technical findings with evidence. Every HIGH and CRITICAL finding reviewed by a senior security engineer before sign-off. SOC 2 / ISO 27001 accepted.',
  },
  {
    num: '04',
    title: 'Fix, Then Verify',
    body: 'The moment you ship a fix, re-scan the selected findings to confirm each one is actually resolved. No new engagement, no extra fee. Your surface is monitored as it changes, not captured once.',
  },
];

const DEMO_BASE = 'https://platform.cyberarmy.ai/demo';
const demoClips = [
  {
    src: `${DEMO_BASE}/2-targets.webm`,
    poster: `${DEMO_BASE}/2-targets.png`,
    title: 'Scan only what you authorize',
    desc: 'Every domain and IP range is verified and consent-based. AutoScan can only touch assets you have proven you own.',
  },
  {
    src: `${DEMO_BASE}/8-results.webm`,
    poster: `${DEMO_BASE}/8-results.png`,
    title: 'Findings, issues, and risks',
    desc: 'The same evidence in three views: raw findings, the issues they group into, and the attack paths they add up to.',
  },
  {
    src: `${DEMO_BASE}/10-tools.webm`,
    poster: `${DEMO_BASE}/10-tools.png`,
    title: 'On-demand checks',
    desc: 'Need one answer fast? Check a URL, port, TLS, DNS, or email posture on its own, without running a full scan.',
  },
];

const SEV = {
  CRIT: 'text-red-400 bg-red-500/10 border-red-500/20',
  HIGH: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  MED:  'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  LOW:  'text-accent bg-accent/10 border-accent/20',
  INFO: 'text-zinc-400 bg-white/5 border-white/10',
};

const sampleFindings = [
  { sev: 'CRIT', cat: 'Web App',      color: SEV.CRIT, exploited: true,  title: 'SQL injection (SQLi)', desc: 'Injectable parameters that let attackers read or alter backend databases: blind, error-based, and union-based SQLi.' },
  { sev: 'HIGH', cat: 'Leaked Creds', color: SEV.HIGH, exploited: false, title: 'Info-stealer credential leaks', desc: 'Employee and customer logins harvested by info-stealer malware and exposed in breach data, driving account takeover (ATO).' },
  { sev: 'HIGH', cat: 'API',          color: SEV.HIGH, exploited: true,  title: 'IDOR / BOLA broken authorization', desc: 'Insecure direct object references that let one account reach data belonging to other accounts through the API.' },
  { sev: 'HIGH', cat: 'API',          color: SEV.HIGH, exploited: true,  title: 'Exposed OpenAPI / Swagger schema', desc: 'Unauthenticated API documentation that maps every endpoint, parameter, and method for attackers.' },
  { sev: 'HIGH', cat: 'Secrets',      color: SEV.HIGH, exploited: true,  title: 'Exposed API keys & secrets', desc: 'Live API keys, tokens, and cloud credentials leaked in responses, JavaScript bundles, and cached pages.' },
  { sev: 'HIGH', cat: 'Source Code',  color: SEV.HIGH, exploited: false, title: 'Secrets in public repositories', desc: 'Access tokens and internal source exposed in public code repositories, GitHub, and open .git directories.' },
  { sev: 'HIGH', cat: 'Network',      color: SEV.HIGH, exploited: true,  title: 'Default & weak credentials', desc: 'Admin panels and services reachable with default or guessable credentials, enabling unauthorized access.' },
  { sev: 'MED',  cat: 'API',          color: SEV.MED,  exploited: true,  title: 'Unauthenticated API endpoints', desc: 'APIs that return sensitive data with no authentication or authorization checks.' },
  { sev: 'MED',  cat: 'CVE',          color: SEV.MED,  exploited: false, title: 'Exploitable CVEs & outdated software', desc: 'Known, remotely exploitable CVEs in outdated or end-of-life internet-facing components.' },
  { sev: 'MED',  cat: 'Cloud',        color: SEV.MED,  exploited: true,  title: 'Public cloud storage buckets', desc: 'Misconfigured S3, GCS, and Azure buckets that allow anonymous listing or download of data.' },
  { sev: 'MED',  cat: 'DNS',          color: SEV.MED,  exploited: false, title: 'Subdomain takeover', desc: 'Dangling DNS and CNAME records pointing to unclaimed cloud resources that attackers can hijack.' },
  { sev: 'MED',  cat: 'TLS',          color: SEV.MED,  exploited: false, title: 'Weak & deprecated TLS / SSL', desc: 'Deprecated TLS 1.0/1.1, weak ciphers, and expired or mis-scoped certificates.' },
  { sev: 'MED',  cat: 'CORS',         color: SEV.MED,  exploited: true,  title: 'Misconfigured CORS', desc: 'Permissive cross-origin resource sharing that enables cross-site theft of authenticated data.' },
  { sev: 'MED',  cat: 'Exposure',     color: SEV.MED,  exploited: false, title: 'Exposed staging & dev environments', desc: 'Non-production, debug, and admin interfaces reachable from the public internet.' },
  { sev: 'LOW',  cat: 'Email',        color: SEV.LOW,  exploited: false, title: 'Email spoofing (SPF / DKIM / DMARC)', desc: 'Missing or non-enforcing email authentication that allows domain spoofing and phishing.' },
  { sev: 'LOW',  cat: 'Headers',      color: SEV.LOW,  exploited: false, title: 'Missing security headers', desc: 'Absent HSTS, CSP, X-Content-Type-Options, and Referrer-Policy that weaken browser-side defenses.' },
];

const checks = [
  {
    title: 'Leaked Credentials & Info-Stealer Intel',
    items: ['Credentials in info-stealer logs', 'Employee account compromise', 'Breach-corpus credential matches', 'Credential-spray and ATO exposure'],
  },
  {
    title: 'Exposed Secrets & API Keys',
    items: ['API keys and tokens in responses', 'Secrets in JavaScript bundles', 'Cloud credentials exposed', 'Cached and archived secret leaks'],
  },
  {
    title: 'Source Code & Repositories',
    items: ['Public code repositories', 'Secrets committed to repos', 'Exposed .git directories', 'Leaked internal source'],
  },
  {
    title: 'Code Scanning (SAST)',
    items: ['Vulnerabilities in source', 'Insecure dependencies', 'Hardcoded secrets in code', 'Client-side JavaScript flaws'],
  },
  {
    title: 'API Security',
    items: ['Unauthenticated API endpoints', 'Exposed OpenAPI / Swagger schemas', 'Broken object-level auth (IDOR / BOLA)', 'Excessive data exposure'],
  },
  {
    title: 'Web Application (OWASP Top 10)',
    items: ['SQL and command injection', 'XSS and CSRF', 'SSRF and open redirect', 'Auth and session flaws'],
  },
  {
    title: 'Known CVEs & Vulnerable Software',
    items: ['Remotely exploitable CVEs', 'Outdated components', 'End-of-life software', 'Default and known credentials'],
  },
  {
    title: 'Network & Exposed Services',
    items: ['Open port exposure', 'Unprotected admin panels', 'SSH / RDP / SMB / databases', 'Unauthenticated services'],
  },
  {
    title: 'TLS & Certificates',
    items: ['Deprecated TLS 1.0 / 1.1', 'Weak cipher suites', 'Expired or mis-scoped certs', 'Certificate name mismatch'],
  },
  {
    title: 'DNS & Email Security',
    items: ['Subdomain takeover', 'Dangling CNAME records', 'SPF / DKIM / DMARC', 'DNSSEC and MTA-STS'],
  },
  {
    title: 'Cloud & Infrastructure',
    items: ['Public S3 / GCS buckets', 'Cloud origin exposure', 'IMDS credential theft', 'Misconfigured storage'],
  },
  {
    title: 'CORS, Headers & Config',
    items: ['Permissive CORS policies', 'Missing security headers', 'Clickjacking and SRI gaps', 'Staging and debug exposure'],
  },
];

const compare = [
  { label: 'Time to first result', autoscan: '20 minutes', manual: '2-4 weeks', scanner: 'Hours' },
  { label: 'Price', autoscan: 'Contact us', manual: '$15K-$50K', scanner: '$5K-$50K/yr' },
  { label: 'Active exploitation', autoscan: true, manual: true, scanner: false },
  { label: 'Human verification of priority findings', autoscan: true, manual: true, scanner: false },
  { label: 'Executive summary', autoscan: true, manual: true, scanner: false },
  { label: 'Re-test after fixes', autoscan: true, manual: 'Extra fee', scanner: true },
  { label: 'Zero false positives', autoscan: true, manual: true, scanner: false },
  { label: 'SOC 2 / ISO 27001', autoscan: true, manual: true, scanner: false },
];

const faqs = [
  {
    q: 'Will my auditor accept this for SOC 2 / ISO 27001?',
    a: "Yes. The PDF report documents scope, methodology, findings with evidence, and remediation guidance. The same structure auditors expect from a traditional pentest engagement. We've designed the report specifically for compliance use.",
  },
  {
    q: 'Is this a real penetration test or just a vulnerability scan?',
    a: "It's a real penetration test. We actively attempt to exploit vulnerabilities. Anonymous FTP logins are tested, default credentials are tried, SQL injection payloads are sent. Every finding has real evidence.",
  },
  {
    q: 'How do I start, and can I scope by IP ranges or subdomains?',
    a: 'Enter a domain and go, we handle the rest: discovery, mapping, and testing of your external surface. You can optionally add IP ranges or specific subdomains to expand or narrow the scope to exactly what you want tested.',
  },
  {
    q: 'Can I verify a fix without running a whole new test?',
    a: 'Yes. After you fix an issue, re-scan just the findings you changed to confirm each one is actually resolved. It is included at no extra cost, and your surface keeps being monitored as it changes, so new exposure is caught early rather than at the next annual engagement.',
  },
  {
    q: 'How do you prevent AI hallucinations and false positives?',
    a: 'Two layers. First, every finding must be backed by evidence from an actual exploitation attempt - if the payload did not work, it does not go in the report. Second, a senior security engineer reviews every HIGH and CRITICAL finding before the report is finalized. No AI-only findings, ever.',
  },
  {
    q: 'What happens after the report?',
    a: 'Every finding ships with remediation guidance. When you fix something, re-scan just the findings you changed to verify each one is actually resolved, included at no extra cost. If you also run CyberArmy AutoFix, confirmed findings flow into its remediation queue and become permanent regression tests, so a fixed issue cannot quietly come back.',
  },
  {
    q: "What's the difference between AutoScan and Pentera / NodeZero?",
    a: 'Pentera and NodeZero start at $20K-$100K/year targeting enterprise security teams. AutoScan targets startups and growing companies who need the same quality of testing without a six-figure budget or a lengthy procurement process.',
  },
  {
    q: 'Will the scan affect my production systems?',
    a: 'Scan rates are conservative: port scanning is capped at 1,000 packets/second. All exploitation attempts use standard techniques a real attacker would use. Designed to find vulnerabilities, not cause downtime.',
  },
  {
    q: "What doesn't it cover?",
    a: 'AutoScan tests your external attack surface: everything reachable from the public internet. Internal networks behind VPN, authenticated cloud internals, mobile apps, and source code are on our roadmap.',
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.06]">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-sm font-medium text-white/80">{q}</span>
        <span className={`flex-shrink-0 w-5 h-5 rounded-full border border-white/[0.12] flex items-center justify-center transition-transform duration-200 ${open ? 'rotate-45' : ''}`}>
          <svg className="w-3 h-3 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-zinc-400 leading-relaxed max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AutoScanClient() {
  return (
    <div className="bg-surface-base min-h-screen">

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-8 pb-24 sm:pb-32 px-6">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(91, 149, 255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(91, 149, 255,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-accent/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent-secondary/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="mb-10">
            <div className="inline-flex items-center gap-2 text-xs text-zinc-500">
              <Link href="/" className="hover:text-zinc-300 transition-colors">Cyber Army</Link>
              <span>/</span>
              <span className="text-zinc-300">AutoScan</span>
            </div>
          </motion.div>

          <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium tracking-wide uppercase mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              EASM · DAST · SAST · Early Access
            </span>
          </motion.div>

          <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible"
            className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            <span className="text-white/90">Find what you actually</span>
            <br />
            <span className="text-accent">have exposed.</span>
          </motion.h1>

          <motion.p custom={2} variants={fadeUp} initial="hidden" animate="visible"
            className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-10">
            AutoScan is one agentic scanner across your attack surface, apps, and code. It continuously maps what you expose to the internet, tests your running web apps and APIs, and scans your source code, then proves what is actually exploitable with real exploitation: unauthenticated APIs, open ports, TLS errors, admin panels, default credentials, IDOR, SQL injection, SSRF, and more. Every priority finding is validated and signed off by a senior security engineer in an auditor-accepted report, and in the beta ships with AI-suggested fixes. A full pentest in minutes, not once a year.
          </motion.p>

          <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible"
            className="flex flex-wrap gap-3 justify-center mb-5">
            <Link
              href="/beta-signup?product=AutoScan&source=autoscan_hero"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/35 hover:scale-[1.02] active:scale-[0.98]"
            >
              Request Beta Access
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </motion.div>

          <motion.p custom={4} variants={fadeUp} initial="hidden" animate="visible"
            className="text-xs text-zinc-600">
            Backend launching soon · Join the waitlist for early access
          </motion.p>

          {/* Product screenshot: real AutoScan Findings view */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
            className="mt-16 max-w-5xl mx-auto"
          >
            <div className="rounded-2xl border border-white/[0.08] bg-surface-1 overflow-hidden shadow-2xl shadow-black/60">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-surface-deep border-b border-white/[0.06]">
                <span className="w-2.5 h-2.5 rounded-full bg-accent/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
                <span className="ml-2 text-[11px] font-mono text-zinc-500">AutoScan · Findings · LIVE</span>
              </div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/autoscan-findings.png"
                alt="AutoScan Findings dashboard: severity breakdown and deduplicated findings, each backed by exploitation evidence"
                width={1851}
                height={912}
                className="w-full h-auto block"
                loading="eager"
                decoding="async"
              />
            </div>
            <p className="mt-4 text-center text-xs text-zinc-600">
              Findings deduplicated and prioritized by real exploitability, each with evidence and remediation.
            </p>
          </motion.div>
        </div>
      </section>


      {/* ── WHAT AUTOSCAN COVERS ────────────────── */}
      <section className="px-6 py-20 sm:py-28 bg-surface-1 border-y border-white/[0.06]">
        <div className="max-w-6xl mx-auto">
          <InViewSection>
            <div className="text-center mb-14">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">One scanner, full coverage</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">Your attack surface, apps, and code.</h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto">One agentic scanner that covers what used to take three separate tools and a once-a-year pentest.</p>
            </div>
          </InViewSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pillars.map((p, i) => (
              <ScrollFadeIn key={p.tag} delay={i * 60}>
                <div className="h-full rounded-2xl border border-white/[0.06] bg-surface-base hover:border-accent/20 transition-all duration-300 p-6">
                  <span className="inline-block text-xs font-mono font-bold text-accent bg-accent/10 border border-accent/20 rounded px-2 py-0.5 mb-4">{p.tag}</span>
                  <h3 className="text-sm font-semibold text-white/85 mb-2">{p.name}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{p.desc}</p>
                </div>
              </ScrollFadeIn>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-zinc-400">Every finding is validated with real evidence and ships with clear, step-by-step fix instructions.</p>
        </div>
      </section>

      {/* ── SEE IT IN ACTION (demo videos) ───────────── */}
      <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InViewSection>
          <div className="mb-14 text-center">
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">See it in action</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">Watch the real product.</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">Short screen recordings of AutoScan running against a live demo workspace. Real product, not mockups.</p>
          </div>
        </InViewSection>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {demoClips.map((c, i) => (
            <ScrollFadeIn key={c.src} delay={i * 70}>
              <figure className="h-full rounded-2xl border border-white/[0.08] bg-surface-1 overflow-hidden hover:border-accent/20 transition-all duration-300">
                <video
                  controls
                  preload="none"
                  playsInline
                  muted
                  poster={c.poster}
                  aria-label={c.title}
                  className="w-full aspect-video bg-surface-deep block"
                >
                  <source src={c.src} type="video/webm" />
                </video>
                <figcaption className="p-5">
                  <h3 className="text-sm font-semibold text-white/85 mb-1.5">{c.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{c.desc}</p>
                </figcaption>
              </figure>
            </ScrollFadeIn>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            href="/contact?lead_type=demo_access&source=autoscan_demo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-accent/25 bg-accent/[0.06] text-accent font-semibold hover:bg-accent/10 transition-all duration-300 text-sm"
          >
            Request a live demo
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="relative overflow-hidden bg-surface-deep border-y border-accent-secondary/10 py-3">
        <style>{`
          @keyframes ticker-autoscan {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ticker-autoscan-track {
            display: flex;
            width: max-content;
            animation: ticker-autoscan 36s linear infinite;
          }
          .ticker-autoscan-track:hover { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) {
            .ticker-autoscan-track { animation: none; }
          }
        `}</style>
        <div className="ticker-autoscan-track">
          {[
            'SQL INJECTION CONFIRMED',
            'DEFAULT CREDENTIALS EXPLOITED',
            'SUBDOMAIN TAKEOVER FOUND',
            'EXPOSED API KEY DETECTED',
            'TLS 1.0 ACTIVE · DOWNGRADE POSSIBLE',
            'ADMIN PANEL ACCESSIBLE',
            'S3 BUCKET PUBLICLY READABLE',
            'SSRF VULNERABILITY CONFIRMED',
            'OPEN REDIRECT EXPLOITED',
            'DNS MISCONFIGURATION FOUND',
            'CRITICAL CVE EXPLOITABLE',
            'AUTH BYPASS CONFIRMED',
            'SQL INJECTION CONFIRMED',
            'DEFAULT CREDENTIALS EXPLOITED',
            'SUBDOMAIN TAKEOVER FOUND',
            'EXPOSED API KEY DETECTED',
            'TLS 1.0 ACTIVE · DOWNGRADE POSSIBLE',
            'ADMIN PANEL ACCESSIBLE',
            'S3 BUCKET PUBLICLY READABLE',
            'SSRF VULNERABILITY CONFIRMED',
            'OPEN REDIRECT EXPLOITED',
            'DNS MISCONFIGURATION FOUND',
            'CRITICAL CVE EXPLOITABLE',
            'AUTH BYPASS CONFIRMED',
          ].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-3 px-6 text-xs font-mono font-medium tracking-widest text-accent-secondary/70 whitespace-nowrap">
              <span className="w-1 h-1 rounded-full bg-accent-secondary/40 flex-shrink-0" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── PROOF STATS ──────────────────────────────────────────────── */}
      <section className="px-6 py-6 bg-surface-1 border-y border-white/[0.06]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-white/[0.06]">
          {[
            { val: '20 min', label: 'Time to results' },
            { val: '100s', label: 'Tools orchestrated' },
            { val: '0', label: 'False positives' },
            { val: 'SOC 2', label: 'Report accepted' },
          ].map((s, i) => (
            <div key={i} className="py-6 px-6 text-center">
              <div className="text-2xl font-bold text-accent mb-1">{s.val}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InViewSection>
          <div className="mb-16">
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">How It Works</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">Enter a domain. We handle the rest.</h2>
            <p className="text-zinc-400 text-lg max-w-xl">No scheduling. No waiting room. No $15,000 invoice. Pentest, then verify your fixes held.</p>
          </div>
        </InViewSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {howItWorks.map((step, i) => (
            <ScrollFadeIn key={step.num} delay={i * 80}>
              <div className="h-full rounded-2xl border border-white/[0.06] bg-surface-1 hover:bg-surface-2 hover:border-accent/15 transition-all duration-300 p-7">
                <div className="w-10 h-10 rounded-xl bg-accent/8 border border-accent/15 flex items-center justify-center text-accent font-mono text-sm font-bold mb-5">
                  {step.num}
                </div>
                <h3 className="text-base font-semibold text-white/90 mb-3">{step.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{step.body}</p>
              </div>
            </ScrollFadeIn>
          ))}
        </div>
      </section>

      {/* ── WHAT WE TEST ─────────────────────────────────────────────── */}
      <section className="px-6 py-24 sm:py-32 bg-surface-1">
        <div className="max-w-6xl mx-auto">
          <InViewSection>
            <div className="mb-16">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">Coverage</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">What we test.</h2>
              <p className="text-zinc-400 text-lg max-w-xl">A full agentic penetration test across your attack surface, apps, code, and the open web. Hundreds of tools orchestrated, every finding validated by real exploitation, with nightly continuous retesting as your assets change.</p>
            </div>
          </InViewSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {checks.map((cat, i) => (
              <ScrollFadeIn key={cat.title} delay={i * 60}>
                <div className="h-full rounded-2xl border border-white/[0.06] bg-surface-base hover:border-accent/15 transition-all duration-300 p-6">
                  <h3 className="text-sm font-semibold text-white/80 mb-4 pb-3 border-b border-white/[0.06]">{cat.title}</h3>
                  <ul className="space-y-2.5">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-zinc-400">
                        <svg className="w-3.5 h-3.5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXAMPLE FINDINGS ──────────────────── */}
      <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InViewSection>
          <div className="mb-16">
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">Real findings</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">What AutoScan finds.</h2>
            <p className="text-zinc-400 text-lg max-w-2xl">
              The classes of security findings AutoScan surfaces, from leaked credentials and exposed secrets to API flaws, injection, known CVEs, and cloud misconfigurations. This is agentic penetration testing, not a vulnerability scan: findings tagged{' '}
              <span className="text-green-400 font-medium">Confirmed exploited</span> are validated with real, safe exploitation, and each ships with an AI-suggested fix.
            </p>
          </div>
        </InViewSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sampleFindings.map((f, i) => (
            <ScrollFadeIn key={f.title} delay={i * 50}>
              <div className="h-full rounded-2xl border border-white/[0.06] bg-surface-1 hover:border-accent/15 transition-all duration-300 p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${f.color}`}>{f.sev}</span>
                  <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-wide">{f.cat}</span>
                </div>
                <h3 className="text-sm font-semibold text-white/85 mb-1.5 leading-snug">{f.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                {f.exploited ? (
                  <span className="inline-flex items-center gap-1.5 mt-3 text-[10px] font-mono font-semibold text-green-400 bg-green-500/10 border border-green-500/20 rounded px-2 py-0.5">
                    <span className="w-1 h-1 rounded-full bg-green-400" /> Confirmed exploited
                  </span>
                ) : null}
              </div>
            </ScrollFadeIn>
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-zinc-600">Representative finding categories only. Real customer data, targets, and hosts are never published.</p>
      </section>

      {/* ── WHY AI PENTESTING > MANUAL ─────────────────────────────── */}
      <section className="px-6 py-24 sm:py-32 bg-surface-1 border-y border-white/[0.06]">
        <div className="max-w-6xl mx-auto">
          <InViewSection>
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">Why Agentic AI Pentesting</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white/90 tracking-tight mb-4">
              AI scale. Human judgment.<br />
              <span className="text-accent">Faster. Consistent. Verified.</span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-14">
              Manual pentesting has a people problem: scarce senior talent, firms booked out months, quality that varies by tester. AutoScan runs at AI scale and routes every priority finding through a senior security engineer. Speed without giving up judgment.
            </p>
          </InViewSection>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* AI column */}
            <InViewSection>
              <div className="h-full rounded-2xl border border-accent/20 bg-surface-base p-8">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-accent">AutoScan - AI Pentest</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    ['Results in 20 minutes', 'No scheduling, no kickoff calls, no waiting weeks for a slot. Start now, report in 20 minutes.'],
                    ['Always consistent', 'Every scan runs the same checks with the same rigor. No variation between testers, no bad days, no shortcuts.'],
                    ['No access or trust issues', 'Domain verification via DNS TXT record. No VPN access, no shared credentials, no keys handed over.'],
                    ['Evidence-backed, zero noise', 'Every finding was actively exploited. The report contains proof, not theoretical risk scores or scanner dumps.'],
                    ['Senior engineer verification', 'Every HIGH and CRITICAL finding is reviewed and signed off by a named security engineer before the report is finalized. No AI-only findings.'],
                    ['Verify your fixes with a re-scan', 'Ship a fix, then re-scan the selected findings to confirm each one is actually resolved. No new engagement, no extra fee, no scheduling delay.'],
                    ['Comprehensive coverage and monitoring', 'Full external attack surface coverage, monitored continuously as your assets change. Not a one-day snapshot.'],
                    ['SOC 2 / ISO 27001 accepted', 'Auditor-accepted PDF with scope, methodology, findings, and remediation. Built for compliance.'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-3">
                      <svg className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-zinc-300">
                        <span className="font-medium text-white/90">{title}</span>
                        {' - '}<span className="text-zinc-400">{desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </InViewSection>

            {/* Manual column */}
            <InViewSection>
              <div className="h-full rounded-2xl border border-white/[0.06] bg-surface-base p-8">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-8 h-8 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-zinc-400">Traditional Manual Pentest</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    ['2-4 weeks to schedule', 'Kickoff calls, scoping meetings, NDA negotiation, and scheduling coordination before a single test runs.'],
                    ['Scarce senior talent', '(ISC)² estimates roughly 4M unfilled security positions globally, with offensive specialists the scarcest subset. Top firms are booked months out.'],
                    ['Quality varies by tester', 'Skill levels differ across engagements. A junior tester on a tight deadline can miss what a senior catches.'],
                    ['Significant trust exposure', 'You share VPN credentials, API keys, admin access, and internal architecture with a team you just met.'],
                    ['Mostly offshore delivery', 'Many firms use offshore teams for execution. US rates, offshore delivery, less accountability.'],
                    ['Re-test costs extra', 'Fixed a critical issue? A re-test is a new engagement. More scheduling, more cost, more delay.'],
                    ['Point-in-time snapshot', 'A single engagement captures your posture on one day. Your attack surface changes every day.'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-3">
                      <svg className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-zinc-500">
                        <span className="font-medium text-zinc-400">{title}</span>
                        {' - '}{desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </InViewSection>
          </div>

          {/* Trust callout */}
          <InViewSection>
            <div className="mt-6 rounded-2xl border border-accent/10 bg-accent/[0.03] p-7">
              <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl">
                <span className="text-white/90 font-medium">On trust: </span>
                Manual pentests require you to hand over VPN access, API keys, admin credentials, and internal network diagrams to a team you just met. AutoScan verifies ownership via a DNS record - the same mechanism Google uses. We never see your credentials, never enter your internal systems, and never touch anything you have not explicitly authorised.
              </p>
            </div>
          </InViewSection>
        </div>
      </section>

      {/* ── COMPARISON TABLE ──────────────────────────────────────────── */}
      <section className="px-6 py-24 sm:py-32 max-w-6xl mx-auto">
        <InViewSection>
          <div className="mb-12">
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">Why AutoScan</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight">Compared to the alternative.</h2>
          </div>
        </InViewSection>

        <InViewSection>
          <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-6 py-4 text-zinc-500 font-medium w-1/4" />
                    <th className="px-6 py-4 text-center bg-accent/[0.04] border-x border-accent/15">
                      <span className="text-accent font-semibold">AutoScan</span>
                    </th>
                    <th className="px-6 py-4 text-center text-zinc-400 font-medium">Manual pentest firm</th>
                    <th className="px-6 py-4 text-center text-zinc-400 font-medium">Vuln scanner</th>
                  </tr>
                </thead>
                <tbody>
                  {compare.map((row, i) => (
                    <tr key={row.label} className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                      <td className="px-6 py-4 text-zinc-400 font-medium">{row.label}</td>
                      <td className="px-6 py-4 text-center bg-accent/[0.03] border-x border-accent/10">
                        {typeof row.autoscan === 'boolean' ? (
                          row.autoscan
                            ? <svg className="w-4 h-4 text-accent mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                            : <svg className="w-4 h-4 text-zinc-600 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        ) : <span className="font-medium text-accent">{row.autoscan}</span>}
                      </td>
                      <td className="px-6 py-4 text-center text-zinc-500">
                        {typeof row.manual === 'boolean' ? (
                          row.manual
                            ? <svg className="w-4 h-4 text-zinc-400 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                            : <svg className="w-4 h-4 text-zinc-700 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414-1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        ) : <span>{row.manual}</span>}
                      </td>
                      <td className="px-6 py-4 text-center text-zinc-500">
                        {typeof row.scanner === 'boolean' ? (
                          row.scanner
                            ? <svg className="w-4 h-4 text-zinc-400 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                            : <svg className="w-4 h-4 text-zinc-700 mx-auto" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414-1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                        ) : <span>{row.scanner}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </InViewSection>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section id="faq" className="px-6 py-24 sm:py-32 max-w-3xl mx-auto">
        <InViewSection>
          <div className="text-center mb-16">
            <p className="text-xs font-medium text-accent uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight">Common questions.</h2>
          </div>
        </InViewSection>

        <InViewSection>
          <div className="divide-y divide-white/[0.06]">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </InViewSection>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="px-6 py-24 sm:py-32 max-w-5xl mx-auto">
        <InViewSection>
          <div className="relative rounded-2xl border border-accent/15 overflow-hidden p-12 sm:p-20 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1e40]/30 via-surface-deepest to-[#080A0F] pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-emerald-500/40 to-transparent" />
            <div className="relative">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-4">Get Started</p>
              <h2 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-5">
                Know what attackers<br />can see. Right now.
              </h2>
              <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10">
                Free during beta. Join early access, we respond within 1 business day.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  href="/beta-signup?product=AutoScan&source=autoscan_cta"
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/35 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Request Beta Access
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  href="/autofix"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all duration-300 hover:bg-white/[0.03]"
                >
                  Also try AutoFix →
                </Link>
              </div>
            </div>
          </div>
        </InViewSection>
      </section>
    </div>
  );
}
